<script setup>
    // Libraries
    import { storeToRefs } from 'pinia';
    const { signOut, isLoggedIn } = useAuth()

    // Stores
    import { useUserStore } from '@/stores/user';
    const userStore = useUserStore();

    // Props
    const props = defineProps({
        items: {
            type: Array,
            required: false,
            default: [],
        },
        title: {
            type: String,
            required: false,
            default: 'Itineraries',
        },
        titleClass: {
            type: String,
            required: false,
            default: 'headline-1',
        },
        showViewAll: {
            type: Boolean,
            required: false,
            default: true,
        },
        includeFilters: {
            type: Boolean,
            required: false,
            default: false,
        },

        showHeader: {
            type: Boolean,
            required: false,
            default: true,
        },

        headline: {
            type: String,
            required: false,
            default: 'h2',
        },
    });
    
    const { includeFilters, items } = toRefs(props);

    // Variables
    // only show neighborhoods tab if there are neighborhood itineraries
    const showNeighborhoodsTab = items.value.filter((tour) => tour?.fields?.['Type'] === 'Trail').length ? 'neighborhood' : false;

    const tabs = [
        { label: 'All', value: 'all' },
        { label: 'My Itineraries', value: 'itineraries' },
        { label: 'My Favorites', value: 'favorites' },
        { label: 'Neighborhood', value: showNeighborhoodsTab },
        { label: 'Themed', value: 'themed' },
    ];

    const { $listen } = useNuxtApp();

    // const ohcFavoriteTours = ref([]);
    const selectedItinerary = ref(null);
    const activeTab = ref(tabs[0].value);
    const newItineraryModalIsOpen = ref(false);
    const { tours: userTours } = storeToRefs(userStore);

    // Computed
    const filteredTours = computed(() => {
        let results = items.value;
        if (results && results.length) {
            if (activeTab.value === 'itineraries') {
                results = results.filter((tour) => !tour.fields);
            }
            if (activeTab.value === 'favorites') {
                results = results.filter((tour) => {
                    return userTours.value.includes(tour.id);
                });
            } else if (activeTab.value === 'neighborhood') {
                results = results.filter((tour) => tour?.fields?.['Type'] === 'Trail');
            } else if (activeTab.value === 'themed') {
                results = results.filter((tour) => tour?.fields?.['Type'] === 'Listicle');
            }

            return results;
        }
    });

    // Methods
    const updateTab = (val) => {
        activeTab.value = val;
    };

    function openItineraryModal() {
        newItineraryModalIsOpen.value = true;
        document.body.style.overflow = 'hidden';

        return newItineraryModalIsOpen;
    }

    function closeDetail() {
        selectedItinerary.value = null;
    }

    function handleSelection(val) {
        selectedItinerary.value = val;
    }

    // Lifecycle
    onMounted(() => {
        if (import.meta.client) {
            $listen('globalHeaderNavOpen', () => {
                closeDetail();
            });
        }
    });
</script>

<template>
    <section class="tours-list">
        <div class="container">
            <div class="component-header" v-if="showHeader">
                <h1 v-if="headline == 'h1'" :class="titleClass">{{ title }}</h1>
                <h2 v-else :class="titleClass">{{ title }}</h2>
                <NuxtLink v-if="showViewAll" to="/itineraries" class="view-all">View All</NuxtLink>
            </div>
        </div>

        <ul class="tab-button-row" v-if="includeFilters">
            <li v-for="item in tabs" :key="item.value">
                <button v-if="item.value" class="tab-button" @click="updateTab(item.value)" :class="{ active: activeTab === item.value }">
                    {{ item.label }}
                </button>
            </li>
        </ul>
        <div class="container">
            <div class="login-note">
                <p class="body-book app-text" v-if="activeTab == 'itineraries'">
                    <template v-if="!isLoggedIn">
                        <NuxtLink to="/login" class="turquoise-alert-copy">Log in to My OHC</NuxtLink> before saving sites or itineraries to access them across devices. If you don't see your favorites, make sure you're logged in here and on the device where you first saved them.
                    </template>
                    <template v-else>
                        <strong>You're logged in to My OHC</strong>. Any sites or itineraries you save here will sync across devices. If you don't see items saved elsewhere, be sure you were logged in on that device when you saved them. <a href="#" @click.prevent="signOut" class="turquoise-alert-copy">Log out</a>.
                    </template>         
                </p>
                <p class="body-book app-text" v-if="activeTab === 'favorites'">
                    <template v-if="!isLoggedIn">
                        <NuxtLink to="/login" class="turquoise-alert-copy">Log in to My OHC</NuxtLink> before saving sites or itineraries to access them across devices. If you don't see your favorites, make sure you're logged in here and on the device where you first saved them.
                    </template>
                    <template v-else>
                        <strong>You're logged in to My OHC</strong>. Any sites or itineraries you save here will sync across devices. If you don't see items saved elsewhere, be sure you were logged in on that device when you saved them. <a href="#" @click.prevent="signOut" class="turquoise-alert-copy">Log out</a>.
                    </template>
                </p>
            </div>            
            <template v-if="filteredTours && filteredTours.length < 1">
                <p class="no-results body-book" v-if="activeTab === 'itineraries'">
                    Looks like you haven't created any itineraries yet.
                    <NuxtLink class="body-link" to="/my-ohc/itineraries">Go ahead and make some!</NuxtLink>
                </p>
                <p v-else-if="activeTab === 'favorites'" class="no-results body-book">
                    Looks like you haven't favorited any itineraries yet.
                    <NuxtLink class="body-link" to="/itineraries" @click="updateTab('all')">Go ahead and favorite some!</NuxtLink>
                </p>
                <p v-else class="no-results body-book">No itineraries found.</p>
            </template>
            <ul class="card-list">
                <li v-for="(item, index) in filteredTours" :key="`tour-${item.id}` + index">
                    <template v-if="item.id">
                        <CardsTour :item="item" />
                    </template>
                    <template v-else>
                        <CardsItinerary class="itinerary-item" :itinerary="item" toursVersion @itinerarySelected="handleSelection" />
                    </template>
                </li>
            </ul>
        </div>

        <!-- View single Itinerary. -->
        <GlobalSingleItinerary @save="openItineraryModal" v-if="selectedItinerary !== null" :itinerary="selectedItinerary" @closeItineraryDetail="closeDetail" />
    </section>
</template>

<style lang="scss">
    
    .tours-list {
        padding: 0 0 30px 0;

        .tab-button-row {
            margin-bottom: 20px;

            @media (min-width: $tablet) {
                overflow-x: auto;
            }

            button {
                transition: 0.25s ease;
            }

            button.active {
                background-color: black;
                color: white;
            }
        }

        .login-note {
            margin: 16px auto;

            @media (min-width: $tablet) {
                margin: 32px auto;
            }

            a {
                color: $red;
                text-decoration: none;
                font-weight: 500;

                &:hover {
                    color: $turquoise;
                }
            }
        }

        .card-list li {
            .itinerary-item {
                border: 1px solid #d1d1d1;

                &.itinerary-card {
                    position: relative;
                    figure {
                        position: absolute;
                        width: 35%;
                        top: 0;
                        bottom: 0;
                        left: 0;
                        overflow: hidden;
                        background: url('/img/teal-bg.svg') center/cover no-repeat;
                        img {
                            display: block;
                            width: 100%;
                            height: 100%;
                            object-fit: contain;
                            object-position: center;
                        }
                    }
                    .site-data {
                        width: 65%;
                        margin-left: auto;
                    }
                }
            }
            + li {
                margin-top: 20px;
            }
        }

        .no-results {
            text-transform: none;
            line-height: normal;
        }

        .is-frame .component-header {
            padding-top: 16px;
        }

        @media (min-width: $tablet) {
            padding: 0 0 20px 0;

            .card-list li + li {
                margin-top: 0;
            }

            .card-list {
                display: grid;
                grid-template-columns: repeat(4, 1fr);
                grid-gap: 20px;

                li .itinerary-item.itinerary-card {
                    height: 100%;

                    button {
                        flex-direction: column;
                        height: 100%;
                    }

                    figure {
                        position: relative;
                        width: 100%;
                        height: 200px;
                        flex-shrink: 0;
                    }

                    .site-data {
                        width: 100%;
                        margin-left: 0;
                        height: 100%;
                    }
                }
            }

            h1 {
                font-size: 60px;
            }

            .component-header {
                margin-bottom: 20px;
                padding-top: 0;
            }
        }

        @media (min-width: $desktop) {
            padding: 0px 0px 20px;

            .component-header {
                padding-top: 16px;
            }
        }

        .app-text {
            line-height: normal;
            margin: 15px 0;
        }
    }
</style>
